import React, { useState, useRef } from 'react';
import { 
  Button, 
  Input, 
  List, 
  Modal, 
  Form, 
  InputNumber, 
  Select, 
  Upload, 
  Space, 
  Tag, 
  notification,
  Popconfirm,
  Card
} from 'antd';
import { 
  PlusOutlined, 
  UploadOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  UserOutlined,
  FileExcelOutlined,
  DownloadOutlined
} from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;

const StudentManager = ({ students, onStudentsChange, seatingLayout = [] }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [form] = Form.useForm();
  const fileInputRef = useRef(null);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [currentStudent, setCurrentStudent] = useState(null);

  const showAddModal = () => {
    setEditingStudent(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const showEditModal = (student) => {
    setEditingStudent(student);
    form.setFieldsValue(student);
    setIsModalVisible(true);
  };

  const handleSubmit = (values) => {
    const studentData = {
      id: editingStudent?.id || Date.now(),
      ...values
    };

    if (editingStudent) {
      // 编辑现有学生
      const updatedStudents = students.map(s => 
        s.id === editingStudent.id ? studentData : s
      );
      onStudentsChange(updatedStudents);
      notification.success({ message: '学生信息更新成功' });
    } else {
      // 添加新学生
      onStudentsChange([...students, studentData]);
      notification.success({ message: '学生添加成功' });
    }

    setIsModalVisible(false);
    form.resetFields();
  };

  const deleteStudent = (studentId) => {
    const updatedStudents = students.filter(s => s.id !== studentId);
    onStudentsChange(updatedStudents);
    notification.success({ message: '学生删除成功' });
  };

  const batchAddStudents = (studentList) => {
    // 兼容CSV/TXT，支持中文逗号，容错空行和表头
    const normalizeNumber = (val) => {
      if (val === undefined || val === null) return null;
      const s = String(val).replace(/[^0-9.\-]/g, '').trim();
      if (!s) return null;
      const n = s.includes('.') ? parseFloat(s) : parseInt(s, 10);
      return Number.isFinite(n) ? n : null;
    };

    const parseLine = (line, index) => {
      const trimmed = line.trim();
      if (!trimmed) return null;
      // 跳过表头
      if (/^姓名[ ,，]/.test(trimmed) || /^name[ ,，]/i.test(trimmed)) return null;

      // 替换中文逗号，按逗号或空白切分
      const text = trimmed.replace(/，/g, ',').replace(/\uFEFF/g, '');
      let parts;
      if (text.includes(',')) {
        parts = text.split(',').map(s => s.trim());
      } else {
        parts = text.split(/\s+/).map(s => s.trim());
      }

      const name = parts[0] || `学生${index + 1}`;
      const genderRaw = (parts[1] || '').toString().trim();
      const gender = /^(女|female)$/i.test(genderRaw) ? 'female' : 'male';
      const height = normalizeNumber(parts[2]);
      const grade = normalizeNumber(parts[3]);

      return {
        id: Date.now() + index,
        name,
        gender,
        height,
        grade,
      };
    };

    const lines = studentList
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .split('\n');

    const parsed = [];
    lines.forEach((line, idx) => {
      const s = parseLine(line, idx);
      if (s) parsed.push(s);
    });

    if (parsed.length === 0) {
      notification.warning({ message: '没有可导入的数据', description: '请检查文件格式或内容' });
      return;
    }

    onStudentsChange([...students, ...parsed]);
    notification.success({ 
      message: '批量添加成功', 
      description: `成功添加 ${parsed.length} 名学生` 
    });
  };

  const importFromFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = (e.target.result || '').toString().replace(/^\uFEFF/, ''); // 去除BOM
        if (file.name.endsWith('.json')) {
          const data = JSON.parse(text);
          if (Array.isArray(data)) {
            onStudentsChange([...students, ...data]);
            notification.success({ message: 'JSON文件导入成功' });
          }
        } else if (file.name.endsWith('.csv') || file.name.endsWith('.txt')) {
          batchAddStudents(text);
        }
      } catch (error) {
        notification.error({ message: '文件格式错误', description: error.message });
      }
    };
    reader.readAsText(file);
    return false; // 阻止上传
  };

  const exportStudents = () => {
      const csvContent = [
        '姓名,性别,身高,成绩',
        ...students.map(s => 
          `${s.name},${s.gender === 'female' ? '女' : '男'},${s.height || ''},${s.grade || ''}`
        )
      ].join('\n');
    
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `学生名单_${new Date().toLocaleDateString().replace(/\//g, '-')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    
    notification.success({ message: '学生名单导出成功' });
  };

  const exportTemplate = () => {
    const templateContent = [
      '姓名,性别,身高,成绩',
      '张三,男,175,85.5',
      '李四,女,165,92.0',
      '王五,男,170,78.5',
      '赵六,女,160,88.0',
      '钱七,男,168,82.0'
    ].join('\n');
    
    const blob = new Blob(['\uFEFF' + templateContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = '学生名单导入模板.csv';
    link.click();
    URL.revokeObjectURL(url);
    
    notification.success({ 
      message: '模板下载成功', 
      description: '请按照模板格式填写学生信息后导入'
    });
  };

  const getGenderColor = (gender) => {
    return gender === 'female' ? 'pink' : 'blue';
  };

  // 过滤掉已经安排座位的学生
  const getAvailableStudents = () => {
    const seatedStudentIds = new Set();
    
    // 收集所有已安排座位的学生ID
    seatingLayout.forEach(row => {
      row.forEach(seat => {
        if (seat.student) {
          seatedStudentIds.add(seat.student.id);
        }
      });
    });
    
    // 返回未安排座位的学生
    return students.filter(student => !seatedStudentIds.has(student.id));
  };

  const availableStudents = getAvailableStudents();

  const BatchAddModal = () => {
    const [batchText, setBatchText] = useState('');
    const [visible, setVisible] = useState(false);

    return (
      <>
        <Button icon={<PlusOutlined />} onClick={() => setVisible(true)}>
          批量添加
        </Button>
        <Modal
          title="批量添加学生"
          open={visible}
          onCancel={() => setVisible(false)}
          onOk={() => {
            if (batchText.trim()) {
              batchAddStudents(batchText);
              setBatchText('');
              setVisible(false);
            }
          }}
        >
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              每行一个学生，格式：姓名 性别 身高 成绩
            </p>
            <p className="text-xs text-gray-500">
              示例：张三 男 175 85.5
            </p>
          </div>
          <TextArea
            rows={8}
            placeholder="张三 男 175 85.5&#10;李四 女 165 92.0"
            value={batchText}
            onChange={(e) => setBatchText(e.target.value)}
          />
        </Modal>
      </>
    );
  };

  return (
    <Card 
      title={
        <span>
          <UserOutlined className="mr-2" />
          学生管理 (未安排:{availableStudents.length}/总数:{students.length}人)
        </span>
      }
    >
    <div>
      <div className="mb-4">
        <Space wrap>
          <Button type="primary" icon={<PlusOutlined />} onClick={showAddModal}>
            添加学生
          </Button>
          <BatchAddModal />
          <Upload
            accept=".json,.csv,.txt"
            beforeUpload={importFromFile}
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />}>
              导入文件
            </Button>
          </Upload>
          <Button 
            icon={<FileExcelOutlined />} 
            onClick={exportStudents}
            disabled={students.length === 0}
          >
            导出CSV
          </Button>
          <Button 
            icon={<DownloadOutlined />} 
            onClick={exportTemplate}
            type="dashed"
          >
            下载模板
          </Button>
        </Space>
      </div>

      <List
        dataSource={availableStudents}
        locale={{
          emptyText: availableStudents.length === 0 && students.length > 0 
            ? '所有学生都已安排座位' 
            : '暂无学生数据'
        }}
        renderItem={(student) => (
          <List.Item
            className="draggable hover:bg-gray-50 transition-colors duration-200 border rounded-lg"
            draggable
            onDragStart={(e) => {
              console.log('📋 学生列表拖拽开始:', student.name);
              
              // 设置拖拽数据，与座位拖拽使用相同的格式
              e.dataTransfer.setData('text/plain', JSON.stringify({
                type: 'student',
                ...student
              }));
              e.dataTransfer.effectAllowed = 'move';
              
              console.log('✅ 学生数据设置完成');
              
              // 创建拖拽预览
              const dragImage = e.currentTarget.cloneNode(true);
              dragImage.classList.add('drag-preview');
              dragImage.style.opacity = '0.8';
              dragImage.style.width = e.currentTarget.offsetWidth + 'px';
              dragImage.style.position = 'absolute';
              dragImage.style.top = '-1000px';
              document.body.appendChild(dragImage);
              e.dataTransfer.setDragImage(dragImage, e.currentTarget.offsetWidth / 2, 25);
              
              // 清理拖拽预览
              setTimeout(() => {
                if (document.body.contains(dragImage)) {
                  document.body.removeChild(dragImage);
                }
              }, 1);
            }}
            onDragEnd={(e) => {
              // 清除拖拽状态
              e.currentTarget.classList.remove('dragging');
            }}
            onClick={(e) => {
              // 防止在拖拽时触发点击
              if (!e.currentTarget.classList.contains('dragging')) {
                setCurrentStudent(student);
                setShowStudentModal(true);
              }
            }}
          >
            <div className="w-full">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <UserOutlined className="text-blue-500 cursor-move" title="拖拽到座位表排座位" />
                  <span className="font-medium">{student.name}</span>
                  <Tag color={getGenderColor(student.gender)} size="small">
                    {student.gender === 'female' ? '女' : '男'}
                  </Tag>
                  <div className="flex gap-1 ml-2">
                    <Button 
                      type="text" 
                      icon={<EditOutlined />} 
                      onClick={(e) => {
                        e.stopPropagation();
                        showEditModal(student);
                      }}
                      size="small"
                      className="text-blue-500 hover:bg-blue-50"
                    />
                    <Popconfirm
                      title="确定删除这个学生吗？"
                      onConfirm={(e) => {
                        e.stopPropagation();
                        deleteStudent(student.id);
                      }}
                      okText="删除"
                      cancelText="取消"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button 
                        type="text" 
                        icon={<DeleteOutlined />} 
                        danger
                        size="small"
                        className="text-red-500 hover:bg-red-50"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Popconfirm>
                  </div>
                </div>
              </div>
              <div className="text-sm text-gray-600 flex gap-4">
                {student.height && <span>身高: {student.height}cm</span>}
                {student.grade !== null && student.grade !== undefined && <span>成绩: {student.grade}分</span>}
              </div>
            </div>
          </List.Item>
        )}
      />

      {/* 学生详情弹窗 */}
      <Modal
        title="学生信息"
        open={showStudentModal}
        onCancel={() => setShowStudentModal(false)}
        footer={[
          <Button key="edit" type="primary" onClick={() => {
            showEditModal(currentStudent);
            setShowStudentModal(false);
          }}>
            编辑
          </Button>,
          <Button key="close" onClick={() => setShowStudentModal(false)}>
            关闭
          </Button>
        ]}
      >
        {currentStudent && (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <UserOutlined className="text-4xl text-blue-500" />
              <div>
                <h3 className="text-xl font-bold">{currentStudent.name}</h3>
                <Tag color={getGenderColor(currentStudent.gender)}>
                  {currentStudent.gender === 'female' ? '女' : '男'}
                </Tag>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {currentStudent.height && (
                <div className="bg-gray-50 p-3 rounded">
                  <div className="text-sm text-gray-600">身高</div>
                  <div className="text-lg font-medium">{currentStudent.height}cm</div>
                </div>
              )}
              
              {currentStudent.grade !== null && currentStudent.grade !== undefined && (
                <div className="bg-gray-50 p-3 rounded">
                  <div className="text-sm text-gray-600">成绩</div>
                  <div className="text-lg font-medium">{currentStudent.grade}分</div>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title={editingStudent ? "编辑学生" : "添加学生"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ gender: 'male' }}
        >
          <Form.Item
            label="姓名"
            name="name"
            rules={[{ required: true, message: '请输入学生姓名' }]}
          >
            <Input placeholder="请输入学生姓名" />
          </Form.Item>

          <Form.Item
            label="性别"
            name="gender"
          >
            <Select>
              <Option value="male">男</Option>
              <Option value="female">女</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="身高 (cm)"
            name="height"
          >
            <InputNumber min={100} max={220} placeholder="身高（厘米）" />
          </Form.Item>

          <Form.Item
            label="成绩"
            name="grade"
          >
            <InputNumber min={0} max={100} precision={1} placeholder="成绩" />
          </Form.Item>


          <Form.Item className="text-right mb-0">
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {editingStudent ? "更新" : "添加"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
    </Card>
  );
};

export default StudentManager;
